import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { AiOutlineInfo } from "react-icons/ai";

import {
  HStack,
  IconButton,
  SocialShare,
  IconButtonProps,
  FiShare2,
  Flex,
  ConditionalWrap,
  useBreakpointValue,
  PlacementWithLogical
} from "@/storybook-components";

import SortContent from "../actions/sort";
import SearchContent from "../actions/search";
import PostDrawer from "../actions/create/post-drawer";
import { useAppSelector } from "@/redux/store/hooks";

const StickyActionItems = ({
  sidebarState,
  isOpen,
  toggleSidebar,
  stateHandler,
  setPageNumber,
  searchParams,
  sortParams
}): JSX.Element => {
  const { query } = useRouter();
  const { translate } = useTranslator();
  const { projectId, brainstormerId } = query;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { name, state: brainStormerState } = useAppSelector(
    (state) => state.brainstormers.brainstormer
  );
  const { state: projectState } = useAppSelector(
    (state) => state.project.project
  );

  const wrapFlexChildren = useBreakpointValue({
    base:
      settings.disableSocialMediaSharing ||
      projectState === "archived" ||
      brainStormerState === "archived",
    md: true
  });
  const socialSharePlacement: PlacementWithLogical = useBreakpointValue({
    base: "top",
    xl: "right"
  });

  const IconButtonNode = (props: IconButtonProps) => {
    return (
      <IconButton
        variant="solid"
        w={{ base: 10, sm: 12, xl: 10 }}
        h={{ base: 10, sm: 12, xl: 10 }}
        padding={2.5}
        justifyContent="center"
        alignItems="center"
        borderRadius="50%"
        bg="white"
        color="gray.900"
        boxShadow="lg"
        _hover={{
          background: "white",
          color: "gray.900"
        }}
        _focus={{
          background: "white",
          color: "gray.900"
        }}
        {...props}
      />
    );
  };

  return (
    <HStack
      as="section"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      px={2}
      py={4}
      gap={{ base: 3, sm: 1, md: 4 }}
      position={{ base: "fixed", xl: "sticky" }}
      bottom={{ base: 0, xl: "auto" }}
      top={{ base: "auto", xl: 0 }}
      left={{
        base: 0,
        lg: sidebarState === "expand" ? "52%" : 4,
        xl: sidebarState === "expand" ? "35%" : 4,
        "2xl": sidebarState === "expand" ? "26%" : 6
      }}
      right={{
        base: 0,
        lg: 4,
        "2xl": 6
      }}
      zIndex={5}
      className="ehq-sticky-action"
    >
      <ConditionalWrap condition={wrapFlexChildren} as={Flex} gap={4}>
        <IconButtonNode
          aria-label={
            sidebarState === "expand"
              ? translate("brainstormers.closePanel")
              : translate("brainstormers.openPanel")
          }
          icon={<AiOutlineInfo size={20} />}
          tabIndex={isOpen ? -1 : 0}
          onClick={() => toggleSidebar()}
        />
        {settings.disableSocialMediaSharing ? null : (
          <SocialShare
            title={name}
            placement={socialSharePlacement}
            shareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}`}
            trigger={
              <IconButtonNode
                icon={<FiShare2 />}
                aria-label={translate("common.share")}
              />
            }
          />
        )}
      </ConditionalWrap>
      <ConditionalWrap condition={wrapFlexChildren} as={Flex} gap={4}>
        <SearchContent
          isOpen
          highlightClass="ideas-search-node"
          locale="brainstormers"
          action={stateHandler}
          resetPage={setPageNumber}
          {...searchParams}
        />
        <SortContent
          isOpen
          highlightClass="ideas-search-node"
          locale="brainstormers"
          action={stateHandler}
          resetPage={setPageNumber}
          {...sortParams}
        />
        <PostDrawer />
      </ConditionalWrap>
    </HStack>
  );
};

export default StickyActionItems;
